import {
  Box,
  Button,
  Center,
  HStack,
  IconButton,
  Image,
  Input,
  Radio,
  RadioGroup,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Spacer,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FractionChildProps, FractionProps, PostFraction } from "../Fraction";
import { DisplayMode } from "../../Post";
import eagle from "/eagle.png";
import { ImagePickerModal } from "./select_or_add_image";
import { useState } from "react";
import { FilePlus, ImagePlus } from "lucide-react";

enum ImageAlignment {
  LEFT = "left",
  RIGHT = "right",
  CENTER = "center",
}

export type ImageFractionState = undefined | State;

interface State {
  url: string;
  alt: string;
  alignment?: ImageAlignment;
  shadow?: string;
  width: number;
}

const defaultPorps = {
  url: "",
  alt: "",
  alignment: ImageAlignment.LEFT,
  shadow: "lg",
  width: 500,
};

function ImageEditorAndViewer(props: FractionChildProps<ImageFractionState>) {
  if (props.mode === DisplayMode.EDIT) {
    return <ImageEditor {...props} />;
  } else {
    return <ImageViewer {...props} />;
  }
}

function ImageViewer(props: FractionChildProps<ImageFractionState>) {
  const state = {
    ...(props.state || defaultPorps),
    alignment: props.state?.alignment || ImageAlignment.LEFT,
  };

  return (
    <HStack marginLeft={"15px"}>
      {state.alignment == ImageAlignment.CENTER ? (
        <Center width="100%">
          <Image
            borderRadius={"md"}
            borderWidth={"40px"}
            borderColor={"white"}
            boxShadow={state?.shadow} // Adjust the shadow size by changing the size identifier (e.g., xl, lg, md)
            marginTop={"5px"}
            marginBottom={"5px"}
            maxW={`${state!.width}px`}
            w={"100%"}
            src={state.url}
            alt={state.alt}
            //fallbackSrc="eagle.png"
            fallbackSrc={eagle}
          />
        </Center>
      ) : state.alignment == ImageAlignment.RIGHT ? (
        <HStack width={"100%"}>
          <Spacer />
          <Image
            borderRadius={"md"}
            borderWidth={"40px"}
            borderColor={"white"}
            boxShadow={state?.shadow} // Adjust the shadow size by changing the size identifier (e.g., xl, lg, md)
            marginTop={"5px"}
            marginBottom={"5px"}
            maxW={`${state!.width}px`}
            w={"100%"}
            src={state.url}
            alt={state.alt}
            //fallbackSrc="eagle.png"
            fallbackSrc={eagle}
          />
        </HStack>
      ) : (
        <HStack width={"100%"}>
          <Image
            borderRadius={"md"}
            borderWidth={"40px"}
            borderColor={"white"}
            boxShadow={state?.shadow} // Adjust the shadow size by changing the size identifier (e.g., xl, lg, md)
            marginTop={"5px"}
            marginBottom={"5px"}
            maxW={`${state!.width}px`}
            w={"100%"}
            src={state.url}
            alt={state.alt}
            //fallbackSrc="eagle.png"
            fallbackSrc={eagle}
          />

          <Spacer />
        </HStack>
      )}
    </HStack>
  );
}

function getShadowValue(index: number) {
  const sizes = [undefined, "sm", "md", "lg", "xl", "2xl"];
  return sizes[index];
}

function getSliderValue(shadowVal?: string) {
  const sizes = [undefined, "sm", "md", "lg", "xl", "2xl"];
  return sizes.findIndex((s) => shadowVal === s);
}

function ImageEditor(props: FractionChildProps<ImageFractionState>) {
  const [openImagePicker, setOpenImagePicker] = useState(false);
  return (
    <VStack
      align={"left"}
      spacing={"20px"}
      style={{
        margin: "20px",
        marginTop: "20px",
      }}
    >
      <ImageViewer {...props} />
      <ImagePickerModal 
      prefix="post_"
      onSelect={(url) => {
        if (url != null && props.onChange) {
          props.onChange({...(props.state || defaultPorps), url: url});
        }
      }}
      onClose={() => {setOpenImagePicker(false);}}
      isOpen={openImagePicker}
      />
      <HStack>
        <Text>Url:</Text>
        <Input
          margin={"10px"}
          width="sm"
          textColor={"black"}
          placeholder="https://friidrott.se/foobar"
          value={props.state?.url}
          onChange={(e) => {
            if (props.onChange !== undefined) {
              props.onChange({
                ...(props.state || defaultPorps),
                url: e.target.value,
              });
            }
          }}
        />
        <IconButton onClick={() => {setOpenImagePicker(true);}} aria-label="pickImage"><ImagePlus/></IconButton>
      </HStack>
      <HStack>
        <Text>Position: </Text>
        <RadioGroup
          onChange={(val) => {
            if (props.onChange !== undefined) {
              props.onChange({
                ...(props.state || {
                  url: "",
                  alt: "",
                  alignment: ImageAlignment.LEFT,
                  shadow: "lg",
                  width: 500,
                }),
                alignment: val as ImageAlignment,
              });
            }
          }}
          value={props.state?.alignment}
        >
          <Stack direction="row" textColor={"black"}>
            <Radio value={ImageAlignment.LEFT}>Vänster</Radio>
            <Radio value={ImageAlignment.CENTER}>Mitten</Radio>
            <Radio value={ImageAlignment.RIGHT}>Höger</Radio>
          </Stack>
        </RadioGroup>
      </HStack>
      <HStack>
        <Text>Bredd: </Text>
        <Slider
          onChange={(val) => {
            if (props.onChange !== undefined) {
              props.onChange({
                ...(props.state || defaultPorps),
                width: val,
              });
            }
          }}
          defaultValue={props.state?.width || 500}
          min={100}
          max={2000}
          step={1}
        >
          <SliderTrack bg="red.100">
            <Box position="relative" right={10} />
            <SliderFilledTrack bg="tomato" />
          </SliderTrack>
          <SliderThumb boxSize={6} />
        </Slider>
      </HStack>
      <HStack>
        <Text>Skugga: </Text>
        <Slider
          onChange={(val) => {
            if (props.onChange !== undefined) {
              props.onChange({
                ...(props.state || defaultPorps),
                shadow: getShadowValue(val),
              });
            }
          }}
          defaultValue={getSliderValue(props.state?.shadow)}
          min={0}
          max={5}
          step={1}
        >
          <SliderTrack bg="red.100">
            <Box position="relative" right={10} />
            <SliderFilledTrack bg="tomato" />
          </SliderTrack>
          <SliderThumb boxSize={6} />
        </Slider>
      </HStack>
      <HStack>
        <Text>Bild beskrivning:</Text>
        <Input
          width="lg"
          margin={"10px"}
          textColor={"black"}
          placeholder="Havsörnen, Östhammar Friidrotts logga"
          value={props.state?.alt}
          onChange={(e) => {
            if (props.onChange !== undefined) {
              props.onChange({
                ...(props.state || defaultPorps),
                alt: e.target.value,
              });
            }
          }}
        />
      </HStack>
    </VStack>
  );
}

export const ImageFraction = (props: FractionProps<ImageFractionState>) => {
  return (
    <PostFraction {...props}>
      <ImageEditorAndViewer />
    </PostFraction>
  );
};
